import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { copy } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const inputClass =
  "w-full rounded-none border border-white/15 bg-transparent px-4 py-3.5 font-tty text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-acid";

export const ContactSection = () => {
  const { lang } = useLanguage();
  const t = copy[lang].contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "", consent: false });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.success);
      setForm({ name: "", email: "", company: "", service: "", message: "", consent: false });
    } catch {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="border-b border-white/10" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
        <div className="md:col-span-5">
          <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={0} className="mb-4 font-tty text-xs uppercase tracking-[0.3em] text-acid">
            {t.kicker}
          </motion.p>
          <motion.h2 variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={1} className="font-display text-4xl font-medium tracking-tight text-white md:text-6xl" data-testid="contact-title">
            {t.title}
          </motion.h2>
          <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={2} className="mt-8 max-w-md font-tty text-sm leading-relaxed text-neutral-400">
            {t.subtitle}
          </motion.p>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={3} className="mt-12 space-y-4 font-tty text-sm text-neutral-400">
            <p className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-brand-blue" />
              <span className="text-neutral-500">{t.emailLabel}:</span> hello@libertec.pt
            </p>
            <p className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-brand-green" />
              <span className="text-neutral-500">{t.locationLabel}:</span> {t.location}
            </p>
          </motion.div>
        </div>

        <motion.form
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          custom={2}
          onSubmit={handleSubmit}
          className="space-y-5 md:col-span-7"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-2 block font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t.name}</label>
              <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputClass} />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-2 block font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t.email}</label>
              <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-company" className="mb-2 block font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t.company}</label>
              <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputClass} />
            </div>
            <div>
              <label htmlFor="contact-service" className="mb-2 block font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t.service}</label>
              <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputClass} appearance-none bg-ink`}>
                <option value="">{t.servicePlaceholder}</option>
                {t.serviceOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="contact-message" className="mb-2 block font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t.message}</label>
            <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder={t.messagePlaceholder} className={`${inputClass} resize-none`} />
          </div>
          <label className="flex cursor-pointer items-start gap-3" data-testid="contact-consent-label">
            <input
              type="checkbox"
              required
              checked={form.consent}
              onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
              data-testid="contact-consent-checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-acid"
            />
            <span className="font-tty text-xs leading-relaxed text-neutral-400">{t.consent}</span>
          </label>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="group flex items-center gap-2 bg-acid px-10 py-4 font-tty text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-50"
          >
            {sending ? t.sending : t.submit}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
